import React, { Component } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { withTranslation } from "react-i18next";
import { Table, Button, Grid, Icon, Message } from "semantic-ui-react";
import { Link } from "react-router-dom";
import User from "~/models/User";
import PageStore from "~/stores/PageStore";
import "../styles/PageIndexPage.less";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import ModalDeletePage from "../components/modals/ModalDeletePage";
import ModalPageNotAccessible from "../components/modals/ModalPageNotAccessible";
import ToastMessageStore from "~/stores/ToastMessageStore";
import ToastMessageContainer from "~/components/ToastMessageContainer";
import CustomerStore from "~/stores/CustomerStore";

@observer
class PageIndexPage extends Component {
  constructor(props) {
    super(props);
    document.title = "Pages";
    this.pageStore = new PageStore();
    this.customerStore = new CustomerStore();
    this.state = {
      deleteConfirmation: false,
      deletePageId: null,
      pageNotAccessible: false,
    };
  }

  componentDidMount() {
    const { user, showError } = this.props;
    const { customer } = user;

    this.customerStore.customerId = customer.customerId;
    this.customerStore.load().catch(error => showError(error.message));

    this.pageStore.customerId = customer.customerId;
    this.pageStore.load().catch(error => showError(error.message));
  }

  confirmDelete(deletePageId) {
    this.pageStore.delete(deletePageId);
    const { user } = this.props;
    const { customer } = user;
    this.pageStore.customerId = customer.customerId;
    this.pageStore.load().catch(error => error.message);
    this.closeDeleteModal();
  }

  closeDeleteModal() {
    this.setState({ deleteConfirmation: false });
  }

  deletePage(pageId) {
    this.setState({ deleteConfirmation: true, deletePageId: pageId });
  }

  openModalPageNotAccessible() {
    this.setState({ pageNotAccessible: true });
  }

  closePageNotAccessibleModal() {
    this.setState({ pageNotAccessible: false });
  }

  render() {
    const { t, toastMessageStore } = this.props;
    const { pages, isLoading: pageLoading } = this.pageStore;
    const { customer, isLoading: customerLoading } = this.customerStore;

    if (pageLoading || customerLoading) {
      return <LoadingWrapper loading />;
    }

    const { deleteConfirmation, deletePageId, pageNotAccessible } = this.state;
    const toursPageHref = `http://${customer.canonicalDomain}/customer-portal/${customer.customerId}/tours`;

    return (
      <>
        <div className="page-index-outer-container">
          <ModalDeletePage
            closeModal={() => this.closeDeleteModal()}
            confirmDelete={() => this.confirmDelete(deletePageId)}
            deleteConfirmation={deleteConfirmation}
          />
          <ModalPageNotAccessible
            closeModal={() => this.closePageNotAccessibleModal()}
            pageNotAccessible={pageNotAccessible}
          />
          <Grid stackable columns={2} doubling>
            <Grid.Column floated="left" width={7}>
              <h1>{t("customerPortal:myPages")}</h1>
              <p style={{ fontWeight: "bold" }}>
                {t("customerPortal:createPages")}{" "}
                <a href={toursPageHref} target="_blank" rel="noreferrer">
                  {t("customerPortal:customerPortal")}
                </a>
              </p>
            </Grid.Column>
            <Grid.Column floated="right" width={3} verticalAlign="bottom">
              <Button
                fluid
                style={{ textAlign: "center" }}
                as={Link}
                to="/profile/pages/create"
                color="green"
                icon="add"
                content={t("customerPortal:createPage")}
              />
            </Grid.Column>
          </Grid>
          {!customer.enableCustomerPortal && (
            <Message warning>
              <Message.Header>{t("customerPortal:customerPortalHasBeenDisabled")}</Message.Header>
              <p>
                {t("customerPortal:customerPortalDisabledMessage")} {t("customerPortal:toEnableTheCustomerPortal")},{" "}
                <Link to="/profile/account">{t("customerPortal:goToTheProfileSettingsPage")}</Link>.
              </p>
            </Message>
          )}
          {pages.length > 0 ? (
            <Table celled padded>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell singleLine>{t("customerPortal:title")}</Table.HeaderCell>
                  <Table.HeaderCell singleLine>{t("customerPortal:path")}</Table.HeaderCell>
                  <Table.HeaderCell singleLine textAlign="center">
                    {t("customerPortal:actions")}
                  </Table.HeaderCell>
                  <Table.HeaderCell singleLine textAlign="center">
                    {t("customerPortal:status")}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {pages.map(page => (
                  <Table.Row key={page.pageId}>
                    <Table.Cell width={3}>{page.isToursPage ? t("customerPortal:myTours") : page.title}</Table.Cell>
                    <Table.Cell width={2}>
                      /customer-portal/{customer.customerId}/<strong>{page.isToursPage ? "tours" : page.slug}</strong>
                    </Table.Cell>
                    <Table.Cell width={1}>
                      <Grid columns={1}>
                        <Grid.Column textAlign="center">
                          <Button.Group>
                            {page.isActive ? (
                              <a
                                href={`http://${customer.canonicalDomain}/customer-portal/${customer.customerId}/${
                                  page.isToursPage ? "tours" : page.slug
                                }`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Button
                                  color="grey"
                                  icon="eye"
                                  style={{ borderBottomRightRadius: "0", borderTopRightRadius: "0" }}
                                />
                              </a>
                            ) : (
                              <Button onClick={() => this.openModalPageNotAccessible()} color="grey" icon="eye" />
                            )}

                            <Button
                              color="blue"
                              as={Link}
                              to={page.isToursPage ? "/profile/pages/edit/tours" : `/profile/pages/edit/${page.pageId}`}
                              icon="pen square"
                            />
                            {!page.isToursPage && (
                              <Button color="red" icon="trash alternate" onClick={() => this.deletePage(page.pageId)} />
                            )}
                          </Button.Group>
                        </Grid.Column>
                      </Grid>
                    </Table.Cell>
                    <Table.Cell textAlign="center" width={1}>
                      {page.isActive ? (
                        <Icon size="large" name="check circle" color="green" />
                      ) : (
                        <Icon size="large" name="times circle" color="red" />
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <Message size="huge" style={{ marginTop: "2rem" }}>
              <Message.Header>{t("customerPortal:noPages")}</Message.Header>
              <p>{t("customerPortal:createMessage")}</p>
            </Message>
          )}
          <ToastMessageContainer toastMessageStore={toastMessageStore} />
        </div>
      </>
    );
  }
}

PageIndexPage.propTypes = {
  user: PropTypes.instanceOf(User).isRequired,
  t: PropTypes.func.isRequired,
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
};

export default withTranslation()(ErrorWrapper(PageIndexPage));
